import { useEffect, useState } from "react";
import { api, act, post, patch } from "../lib/api";
import { dur, hrs, istHM, istHM24 } from "../lib/time";
import { TimePick } from "./TimePick";
import { ClientChip, Sheet, Chips } from "./bits";
import { EstimatePicker } from "./EstimatePicker";
const STATES = [["todo", "To do"], ["waiting_client", "With client"], ["waiting_internal", "With team"], ["on_hold", "On hold"],
  ["not_given", "Not given"], ["if_any", "If any"]];

const toMs = (dateIso, hm) => new Date(`${dateIso}T${hm}:00+05:30`).getTime();

function FixTime({ seg, onDone }) {
  const [s, setS] = useState(istHM24(seg.start));
  const [e, setE] = useState(istHM24(seg.end));
  const [reason, setReason] = useState("");
  const [err, setErr] = useState(null);
  async function save() {
    const endDay = e < s ? new Date(new Date(`${seg.date}T12:00:00+05:30`).getTime() + 86400000).toISOString().slice(0, 10) : seg.date;
    const r = await patch(`/segments/${seg.id}`, { start: toMs(seg.date, s), end: toMs(endDay, e), reason, rev: seg.rev });
    if (!r.ok) return setErr(r.error.message);
    await act(Promise.resolve(r));
    onDone();
  }
  return (
    <div className="fix" data-testid={`fix-${seg.id}`}>
      <TimePick value={s} onChange={setS} testid="fix-start" label="Start" />
      <TimePick value={e} onChange={setE} testid="fix-end" label="End" />
      <input className="fld" placeholder="Why? (4 to 140 characters)" value={reason} onChange={(x) => setReason(x.target.value)} data-testid="fix-reason" />
      {err && <p className="err" data-testid="fix-error">{err}</p>}
      <button className="btn-ghost sm" onClick={save} data-testid="fix-save">Fix</button>
    </div>
  );
}

function Stretches({ task, canFix, onChanged }) {
  const [segs, setSegs] = useState([]);
  const [fixing, setFixing] = useState(null);
  useEffect(() => { api(`/tasks/${task.id}/segments`).then((r) => r.ok && setSegs(r.data.items)); }, [task.id]);
  if (!segs.length) return null;
  return (
    <div className="stretches">
      <div className="lbl mt">Every stretch, newest first</div>
      {segs.map((g) => (
        <div key={g.id} className="seg" data-testid={`seg-${g.id}`}>
          <span>{g.date} · {istHM(g.start)}–{g.end ? istHM(g.end) : "now"} · {dur((g.end || Date.now()) - g.start)}</span>
          {g.autoStopped && <span className="auto"> Auto-stopped at {istHM(g.end)}</span>}
          {g.edited && <span className="stag">Fixed</span>}
          {canFix && g.end && <button className="link sm" onClick={() => setFixing(fixing === g.id ? null : g.id)} data-testid={`fix-open-${g.id}`}>Fix this stretch</button>}
          {fixing === g.id && <FixTime seg={g} onDone={onChanged} />}
        </div>
      ))}
    </div>
  );
}

function TakeBack({ task, onDone }) {
  const [reply, setReply] = useState("");
  const [err, setErr] = useState(null);
  async function save(e) {
    e.preventDefault();
    const r = await post(`/tasks/${task.id}/take-back-time`, { reply });
    if (!r.ok) return setErr(r.error.message);
    await act(Promise.resolve(r));
    onDone();
  }
  return (
    <form className="takeback" onSubmit={save} data-testid="take-back-form">
      <div className="lbl mt">Added time</div>
      <textarea className="fld" rows={2} maxLength={140} value={reply} placeholder="To take it back, say why (they see it)"
        aria-label="Reason for taking the time back" onChange={(e) => { setReply(e.target.value); setErr(null); }} data-testid="take-back-reply" />
      {err && <p className="err" role="alert" data-testid="take-back-error">{err}</p>}
      <button type="submit" className="btn-ghost sm" data-testid="take-back-submit">Take it back</button>
    </form>
  );
}

export function CardSheet({ me, task, names, today, tomorrow, onClose, onChanged }) {
  const plan = me.caps.plan;
  const run = async (p) => { const r = await act(p); if (r.ok) onChanged(); };
  const setEst = (estimate) => run(patch(`/tasks/${task.id}`, { rev: task.rev, estimate }));
  const other = task.date === today ? tomorrow : today;
  return (
    <Sheet title="Card details" onClose={onClose} testid="card-sheet">
      <div className="mt-s"><ClientChip code={task.client} name={names[task.client]} label /></div>
      <h2 className="cs-title" data-testid="card-sheet-title">{task.title}</h2>
      <p className="hint">{task.estimateLabel} · logged {dur(task.loggedMs)}{task.remarks ? ` · ${task.remarks}` : ""}</p>
      {task.moreTime && <p className="note" data-testid="card-more-time">Time added: +{hrs(task.moreTime.hours)} · “{task.moreTime.note}”
        {task.moreTime.status === "reverted" && ` · taken back: ${task.moreTime.reply}`}</p>}
      {plan && task.state !== "done" && (
        <>
          <div className="lbl mt">Estimate</div>
          <EstimatePicker testid="card-estimate" apply onPick={setEst} />
          <div className="lbl mt">State</div>
          <Chips items={STATES} testid="card-state" onPick={(s) => run(post(`/tasks/${task.id}/state`, { state: s }))} />
          <div className="cs-actions">
            <button className="btn-ghost sm" onClick={() => run(post(`/tasks/${task.id}/reschedule`, { date: other }))} data-testid="card-reschedule">
              {other === tomorrow ? "Push to the next list" : "Pull into today"}</button>
            <button className="btn-ghost sm danger" onClick={() => run(api(`/tasks/${task.id}`, { method: "DELETE" }))} data-testid="card-delete">Delete</button>
          </div>
        </>
      )}
      {plan && task.moreTime?.status === "added" && <TakeBack task={task} onDone={onChanged} />}
      <Stretches task={task} canFix={plan} onChanged={onChanged} />
    </Sheet>
  );
}
